import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import {
  safePolygon,
  useClick,
  useDismiss,
  useFloating, useHover,
  useInteractions
} from '@floating-ui/react-dom-interactions'
import {
  autoUpdate,
  flip,
  hide,
  shift
} from '@floating-ui/react-dom'

import './index.scss'

const POSITIONS = Object.freeze({
  _TOP: 'top',
  TOP: 'top-start',
  BOTTOM: 'bottom-start',
  LEFT: 'left-start',
  LEFT_BOTTOM: 'left-end',
  RIGHT: 'right-start',
  RIGHT_BOTTOM: 'right-end',
  BOTTOM_END: 'bottom-end',
  BOTTOM_START: 'bottom-start'
})

export const Tooltip = ({
  position,
  anchor,
  children,
  text,
  width,
  maxHeight,
  clickable,
  openOnMount,
  className, // Content classname
  openCallback,
  closeCallback,
  closeDetection,
  canBeHovered,
  delayBeforeClose,
  delayBeforeOpen,
  middleware,
  positionByAnchorChild
}) => {
  const [open, setOpen] = useState(false)
  const { x, y, context, reference, floating, strategy } = useFloating({
    open,
    onOpenChange: (open) => {
      setOpen(open)
      if (closeCallback && !open) closeCallback()
      if (openCallback && open) openCallback()
    },
    strategy: 'fixed',
    placement: position,
    middleware: middleware || [flip(), shift(), hide()],
    whileElementsMounted: (reference, floating, update) => {
      autoUpdate(reference, floating, update, {
        animationFrame: true
      })
    }
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context, {
      enabled: clickable
    }),
    useHover(context, {
      enabled: !clickable,
      handleClose: canBeHovered === false // canBeHovered allows the tooltip to remain open when hovered over, use for tooltips that contain buttons
        ? undefined
        : safePolygon({
          buffer: 1
        }),
      delay: (delayBeforeClose || delayBeforeOpen) ? {
        open: delayBeforeOpen || 0,
        close: delayBeforeClose || 0
      } : undefined
    }),
    useDismiss(context, {
      outsidePointerDown: true
    })
  ])
  const floatContainer = {
    ...getFloatingProps({
      ref: floating,
      style: {
        position: strategy,
        left: x ?? 0,
        top: y ?? 0
      },
      onWheel: (e) => {
        // if this is a Tooltip in the calendar, we want to mouse-scroll the parent calendar section instead of the tooltip itself
        const calendarSection = e.target.closest('.ds-c-section')
        if (calendarSection) {
          calendarSection.scrollTop += e.deltaY
          calendarSection.scrollLeft += e.deltaX
        }
      }
    })

  }

  // hover listeners stay on the .anchor element; with positionByAnchorChild the tooltip is positioned against its first child instead
  // (needed when the child is absolutely positioned or overflows the .anchor, e.g. calendar events in day view)
  const setAnchorRef = useCallback((node) => {
    reference(node)
    if (node && positionByAnchorChild) {
      reference({
        getBoundingClientRect: () => (node.firstElementChild || node).getBoundingClientRect()
      })
    }
  }, [reference, positionByAnchorChild])

  useEffect(() => { openOnMount && setOpen(true) }, [])
  useEffect(() => { !openOnMount && setOpen(false) }, [closeDetection, openOnMount])

  return (
    <div className={classNames('tooltip')}>
      <div className='anchor' {...getReferenceProps({ ref: setAnchorRef })}>
        {anchor}
      </div>
      {open && (
        <div
          className={classNames(
            'detect-zone',
            className,
            { 'text-only': !!text }
          )}
          {...floatContainer}
        >
          <div
            className='content'
            style={{ width, maxHeight }}
          >
            {children}
            {!!text && (
              <div className='text'>
                {text}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

Tooltip.propTypes = {
  position: PropTypes.oneOf(Object.values(POSITIONS)),
  anchor: PropTypes.node.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string
  ]),
  text: PropTypes.string,
  width: PropTypes.string,
  maxHeight: PropTypes.string,
  clickable: PropTypes.bool,
  openOnMount: PropTypes.bool,
  className: PropTypes.string,
  canBeHovered: PropTypes.bool,
  closeDetection: PropTypes.number,
  delayBeforeClose: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.bool
  ]),
  delayBeforeOpen: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.bool
  ]),
  closeCallback: PropTypes.func, // optional callback function that's called when the tooltip is closed
  openCallback: PropTypes.func, // optional callback function that's called when the tooltip is opened
  middleware: PropTypes.arrayOf(PropTypes.object),
  positionByAnchorChild: PropTypes.bool // position the tooltip against the anchor's first child instead of the .anchor wrapper
}
Tooltip.defaultProps = {
  position: POSITIONS.RIGHT,
  width: 'auto',
  maxHeight: 'auto',
  children: null,
  text: null,
  clickable: false,
  openOnMount: false,
  classNames: '',
  delayBeforeClose: false,
  delayBeforeOpen: false,
  canBeHovered: true,
  closeDetection: 0,
  closeCallback: null,
  openCallback: null,
  middleware: undefined,
  positionByAnchorChild: false
}

Tooltip.POSITIONS = POSITIONS
